import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  CircularProgress,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Tooltip,
  Badge,
  Fade,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Description as DocumentIcon,
  AutoAwesome as MagicIcon,
  CheckCircle as CheckIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Gavel as GavelIcon,
  MenuBook as BookIcon,
  FormatQuote as QuoteIcon,
  TextFormat as TextIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Visibility as PreviewIcon,
  CheckCircleOutline,
  HighlightOff,
  Speed as SpeedIcon,
  Psychology as PsychologyIcon,
  Folder as FolderIcon,
  Save as SaveIcon,
  TableChart as TableIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import api from '../../services/api';

// Tipos
interface Suggestion {
  id: string;
  category: 'jurisprudencia' | 'legislacao' | 'argumentacao' | 'formatacao' | 'gramatica' | 'completude';
  severity: 'critical' | 'important' | 'suggestion';
  title: string;
  description: string;
  original_text?: string;
  suggested_text?: string;
  legal_basis?: string;
  applied: boolean;
  rejected: boolean;
}

interface AnalysisResult {
  document_summary: string;
  legal_area: string;
  document_type: string;
  quality_score: number;
  suggestions: Suggestion[];
  statistics: {
    total_words: number;
    total_paragraphs: number;
    jurisprudences_found: number;
    articles_cited: number;
  };
}

const categoryConfig = {
  jurisprudencia: {
    icon: <GavelIcon />,
    label: 'Jurisprudência',
    color: '#1976d2',
    description: 'Sugestões de jurisprudências atualizadas'
  },
  legislacao: {
    icon: <BookIcon />,
    label: 'Legislação',
    color: '#388e3c',
    description: 'Atualização de artigos e leis'
  },
  argumentacao: {
    icon: <QuoteIcon />,
    label: 'Argumentação',
    color: '#7b1fa2',
    description: 'Melhorias na linha argumentativa'
  },
  formatacao: {
    icon: <TextIcon />,
    label: 'Formatação',
    color: '#f57c00',
    description: 'Ajustes de estrutura e formato'
  },
  gramatica: {
    icon: <EditIcon />,
    label: 'Gramática',
    color: '#c62828',
    description: 'Correções ortográficas e gramaticais'
  },
  completude: {
    icon: <WarningIcon />,
    label: 'Completude',
    color: '#0097a7',
    description: 'Seções ou informações faltantes'
  }
};

const severityConfig = {
  critical: { label: 'Crítico', color: 'error' as const },
  important: { label: 'Importante', color: 'warning' as const },
  suggestion: { label: 'Sugestão', color: 'info' as const }
};

// Função para limpar markdown do conteúdo
const cleanMarkdown = (content: string): string => {
  if (!content) return '';
  let cleaned = content;
  // Remove ```markdown ou ``` no início/fim
  cleaned = cleaned.replace(/^```\w*\n?/gm, '');
  cleaned = cleaned.replace(/\n?```$/gm, '');
  // Remove **bold**
  cleaned = cleaned.replace(/\*\*([^*]+)\*\*/g, '$1');
  // Remove *italic*
  cleaned = cleaned.replace(/\*([^*]+)\*/g, '$1');
  // Remove ### headers
  cleaned = cleaned.replace(/#{1,6}\s*/g, '');
  // Remove > blockquotes
  cleaned = cleaned.replace(/^>\s*/gm, '');
  return cleaned.trim();
};

const DocumentImprovementPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Estados
  const [activeStep, setActiveStep] = useState(0);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [extractedText, setExtractedText] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<string>>(new Set());
  const [improvedContent, setImprovedContent] = useState('');
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion | null>(null);
  
  // Estados para preservação de estrutura
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [preserveStructure, setPreserveStructure] = useState(false);
  const [structureInfo, setStructureInfo] = useState<{
    tableCount: number;
    imageCount: number;
    paragraphCount: number;
  } | null>(null);
  
  // Estado para alerta de documento não jurídico
  const [notLegalDocumentInfo, setNotLegalDocumentInfo] = useState<{
    detectedType: string;
    reason: string;
    message: string;
    suggestion: string;
  } | null>(null);

  const steps = ['Importar Documento', 'Analisar Sugestões', 'Revisar e Aplicar', 'Salvar e Editar'];

  // Handlers
  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'text/plain'
    ];

    if (!allowedTypes.includes(file.type)) {
      setError('Formato não suportado. Use PDF, DOCX, DOC ou TXT.');
      return;
    }

    setUploadedFile(file);
    setError(null);
    
    // Extrair texto do arquivo
    await extractTextFromFile(file);
  }, []);

  const extractTextFromFile = async (file: File) => {
    try {
      setIsAnalyzing(true);
      
      const formData = new FormData();
      formData.append('file', file);

      // Para DOCX, usar endpoint com preservação de estrutura
      const isDocx = file.name.toLowerCase().endsWith('.docx');
      const endpoint = isDocx ? '/ai-documents/upload-for-improvement' : '/ai-documents/extract-text';

      const response = await api.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.status === 'success') {
        setExtractedText(response.data.data.text);
        
        // Se for DOCX, guardar informações de preservação
        if (isDocx && response.data.data.session_id) {
          setSessionId(response.data.data.session_id);
          setPreserveStructure(true);
          setStructureInfo({
            tableCount: response.data.data.table_count || 0,
            imageCount: response.data.data.image_count || 0,
            paragraphCount: response.data.data.paragraph_count || 0
          });
        } else {
          setPreserveStructure(false);
          setSessionId(null);
          setStructureInfo(null);
        }
        
        setActiveStep(1);
        
        // Iniciar análise automaticamente
        await analyzeDocument(response.data.data.text);
      }
    } catch (err: any) {
      console.error('Erro ao extrair texto:', err);
      setError('Erro ao processar o arquivo. Tente novamente.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const analyzeDocument = async (text: string) => {
    try {
      setIsAnalyzing(true);
      
      const response = await api.post('/ai-documents/analyze-for-improvement', {
        content: text
      });

      // Verificar se o documento não é jurídico
      if (response.data.status === 'error' && response.data.error_type === 'not_legal_document') {
        const errorData = response.data.data;
        setNotLegalDocumentInfo({
          detectedType: errorData.detected_type || 'Documento não identificado',
          reason: errorData.reason || '',
          message: errorData.message || 'Este documento não é um documento jurídico.',
          suggestion: errorData.suggestion || 'Por favor, importe um documento jurídico.'
        });
        setActiveStep(0); // Voltar ao passo de upload
        return;
      }

      if (response.data.status === 'success') {
        const result = response.data.data as AnalysisResult;
        setAnalysisResult(result);
        setNotLegalDocumentInfo(null); // Limpar alerta anterior se houver
        
        // Pré-selecionar sugestões críticas e importantes
        const autoSelected = new Set<string>();
        result.suggestions.forEach(s => {
          if (s.severity === 'critical' || s.severity === 'important') {
            autoSelected.add(s.id);
          }
        });
        setSelectedSuggestions(autoSelected);
        
        setActiveStep(2);
      }
    } catch (err: any) {
      console.error('Erro ao analisar documento:', err);
      setError('Erro ao analisar o documento. Tente novamente.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleToggleSuggestion = (id: string) => {
    setSelectedSuggestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleApplySuggestions = async () => {
    if (!analysisResult || selectedSuggestions.size === 0) return;

    try {
      setIsApplying(true);
      
      const suggestionsToApply = analysisResult.suggestions.filter(
        s => selectedSuggestions.has(s.id)
      );

      // Usar endpoint com preservação se disponível
      const endpoint = preserveStructure && sessionId 
        ? '/ai-documents/apply-improvements-preserve'
        : '/ai-documents/apply-improvements';
      
      const requestData = preserveStructure && sessionId
        ? { session_id: sessionId, suggestions: suggestionsToApply }
        : { original_content: extractedText, suggestions: suggestionsToApply };

      const response = await api.post(endpoint, requestData);

      if (response.data.status === 'success') {
        // Limpar possíveis marcadores markdown do conteúdo
        const cleanedContent = cleanMarkdown(response.data.data.improved_content);
        setImprovedContent(cleanedContent);
        setActiveStep(3);
      }
    } catch (err: any) {
      console.error('Erro ao aplicar melhorias:', err);
      setError('Erro ao aplicar melhorias. Tente novamente.');
    } finally {
      setIsApplying(false);
    }
  };

  const [isSaving, setIsSaving] = useState(false);
  
  // Estados para diálogo de salvar
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [documentName, setDocumentName] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [folders, setFolders] = useState<Array<{ id: string; name: string }>>([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState<'tradicional' | 'moderno' | 'minimalista'>('tradicional');
  
  // Pasta padrão para documentos melhorados
  const DEFAULT_FOLDER_NAME = 'Revisões com IA';
  
  // Estilos de documento disponíveis
  const documentStyles = [
    {
      id: 'tradicional',
      name: 'Tradicional',
      icon: '⚖️',
      description: 'Formato clássico forense, Times New Roman, espaçamento 1.5',
      preview: {
        font: 'Times New Roman',
        size: '12pt',
        spacing: '1.5'
      }
    },
    {
      id: 'moderno',
      name: 'Moderno',
      icon: '✨',
      description: 'Visual limpo, Calibri, seções destacadas',
      preview: {
        font: 'Calibri',
        size: '11pt',
        spacing: '1.15'
      }
    },
    {
      id: 'minimalista',
      name: 'Minimalista',
      icon: '📄',
      description: 'Direto ao ponto, Arial, espaçamento simples',
      preview: {
        font: 'Arial',
        size: '11pt',
        spacing: '1.0'
      }
    }
  ];
  
  // Carregar pastas quando abrir o diálogo
  const loadFolders = async () => {
    try {
      setLoadingFolders(true);
      const response = await api.get('/documents/folders');
      // A resposta é { status: "success", data: [...] }
      const folderList = response.data?.data || response.data || [];
      // Garantir que é um array
      const foldersArray = Array.isArray(folderList) ? folderList : [];
      setFolders(foldersArray);
      
      // Verificar se a pasta padrão existe
      const defaultFolder = foldersArray.find((f: any) => f.name === DEFAULT_FOLDER_NAME);
      if (defaultFolder) {
        setSelectedFolderId(defaultFolder.id);
      }
    } catch (err) {
      console.error('Erro ao carregar pastas:', err);
      setFolders([]); // Garantir que folders é sempre um array
    } finally {
      setLoadingFolders(false);
    }
  };
  
  // Abrir diálogo de salvar
  const handleOpenSaveDialog = () => {
    // Definir nome padrão baseado no arquivo
    const defaultName = uploadedFile?.name?.replace(/\.[^/.]+$/, '') 
      ? `${uploadedFile.name.replace(/\.[^/.]+$/, '')} (Melhorado)`
      : 'Documento Melhorado';
    setDocumentName(defaultName);
    setSaveDialogOpen(true);
    loadFolders();
  };
  
  const handleSaveToLibrary = async () => {
    try {
      setIsSaving(true);
      setError(null);
      
      // Usar pasta padrão se nenhuma foi selecionada
      let folderId = selectedFolderId;
      
      // Se não há pasta selecionada, criar/usar a pasta padrão
      if (!folderId) {
        try {
          // Tentar criar a pasta padrão (o backend retorna a existente se já houver)
          const createFolderResponse = await api.post('/documents/folders', {
            name: DEFAULT_FOLDER_NAME
          });
          // A resposta é { status: "success", data: { id, name, ... } }
          folderId = createFolderResponse.data?.data?.id || createFolderResponse.data?.id;
        } catch (folderErr: any) {
          // Se deu erro, buscar a pasta existente
          const foldersResponse = await api.get('/documents/folders');
          const folderList = foldersResponse.data?.data || foldersResponse.data || [];
          const foldersArray = Array.isArray(folderList) ? folderList : [];
          const defaultFolder = foldersArray.find((f: any) => f.name === DEFAULT_FOLDER_NAME);
          if (defaultFolder) {
            folderId = defaultFolder.id;
          }
        }
      }
      
      // Converter texto para HTML se não estiver em formato HTML
      // (o backend agora retorna HTML formatado, mas mantemos fallback)
      const ensureHtml = (text: string): string => {
        if (!text) return '';
        // Se já começa com tag HTML, usar direto
        if (text.trim().startsWith('<')) {
          return text;
        }
        // Caso contrário, converter para HTML com parágrafos
        const paragraphs = text.split(/\n\n+/);
        return paragraphs
          .map(p => {
            const trimmed = p.trim();
            if (!trimmed) return '';
            const withBreaks = trimmed.replace(/\n/g, '<br>');
            return `<p>${withBreaks}</p>`;
          })
          .filter(p => p)
          .join('\n');
      };

      const htmlContent = ensureHtml(improvedContent);

      const response = await api.post('/ai-documents/create-simple', {
        title: documentName || `Documento Melhorado - ${new Date().toLocaleDateString()}`,
        content: htmlContent,
        document_type: analysisResult?.document_type || 'peticao_inicial',
        legal_area: analysisResult?.legal_area || 'civil',
        folder_id: folderId,
        style: selectedStyle
      });

      if (response.data.status === 'success') {
        setSaveDialogOpen(false);
        // Navegar para o editor avançado
        navigate(`/documents/editor-advanced/${response.data.data.id}`);
      }
    } catch (err: any) {
      console.error('Erro ao salvar na biblioteca:', err);
      setError(err.response?.data?.detail || 'Erro ao salvar documento. Tente novamente.');
    } finally {
      setIsSaving(false);
    }
  };

  const getSuggestionsByCategory = () => {
    if (!analysisResult) return {};
    
    const grouped: Record<string, Suggestion[]> = {};
    analysisResult.suggestions.forEach(s => {
      if (!grouped[s.category]) {
        grouped[s.category] = [];
      }
      grouped[s.category].push(s);
    });
    return grouped;
  };

  const getSelectedCount = () => {
    return selectedSuggestions.size;
  };

  const getCriticalCount = () => {
    return analysisResult?.suggestions.filter(s => s.severity === 'critical').length || 0;
  };

  // Render Upload Step
  const renderUploadStep = () => (
    <Box sx={{ textAlign: 'center', py: 6 }}>
      {/* Alerta de documento não jurídico */}
      {notLegalDocumentInfo && (
        <Paper
          sx={{
            p: 3,
            mb: 4,
            backgroundColor: alpha('#f44336', 0.08),
            border: '2px solid',
            borderColor: alpha('#f44336', 0.3),
            borderRadius: 3,
            textAlign: 'left'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <WarningIcon sx={{ color: '#f44336', fontSize: 40, mt: 0.5 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ color: '#c62828', fontWeight: 600, mb: 1 }}>
                Documento Não Jurídico Detectado
              </Typography>
              <Typography variant="body1" sx={{ mb: 1.5 }}>
                {notLegalDocumentInfo.message}
              </Typography>
              <Box sx={{ 
                backgroundColor: 'white', 
                p: 2, 
                borderRadius: 2, 
                mb: 2,
                border: '1px solid',
                borderColor: alpha('#f44336', 0.2)
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  <strong>Tipo detectado:</strong> {notLegalDocumentInfo.detectedType}
                </Typography>
                {notLegalDocumentInfo.reason && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Motivo:</strong> {notLegalDocumentInfo.reason}
                  </Typography>
                )}
              </Box>
              <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 500 }}>
                💡 {notLegalDocumentInfo.suggestion}
              </Typography>
              <Button
                variant="outlined"
                color="error"
                size="small"
                sx={{ mt: 2 }}
                onClick={() => {
                  setNotLegalDocumentInfo(null);
                  setUploadedFile(null);
                  setExtractedText('');
                }}
              >
                Tentar outro documento
              </Button>
            </Box>
          </Box>
        </Paper>
      )}
      
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.docx,.doc,.txt"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        id="file-upload-input"
      />
      <Paper
        sx={{
          p: 6,
          border: '2px dashed',
          borderColor: alpha(theme.palette.primary.main, 0.3),
          borderRadius: 4,
          backgroundColor: alpha(theme.palette.primary.main, 0.02),
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          display: 'block',
          '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            transform: 'translateY(-2px)'
          }
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <Box sx={{ mb: 3 }}>
          <UploadIcon sx={{ fontSize: 80, color: theme.palette.primary.main, opacity: 0.8 }} />
        </Box>
        
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Importe seu documento para análise
        </Typography>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Arraste um arquivo ou clique para selecionar
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap', mb: 3 }}>
          <Chip icon={<DocumentIcon />} label="PDF" variant="outlined" />
          <Chip icon={<DocumentIcon />} label="DOCX" variant="outlined" />
          <Chip icon={<DocumentIcon />} label="DOC" variant="outlined" />
          <Chip icon={<DocumentIcon />} label="TXT" variant="outlined" />
        </Box>

        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<UploadIcon />}
          onClick={(e) => {
            e.stopPropagation();
            fileInputRef.current?.click();
          }}
          sx={{ mt: 2 }}
        >
          Selecionar Arquivo
        </Button>
      </Paper>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          O que nossa IA irá analisar:
        </Typography>
        
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {Object.entries(categoryConfig).map(([key, config]) => (
            <Grid item xs={12} sm={6} md={4} key={key}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Box sx={{ color: config.color, mb: 1 }}>{config.icon}</Box>
                  <Typography variant="subtitle2" fontWeight="bold">{config.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{config.description}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );

  // Render Analyzing Step
  const renderAnalyzingStep = () => (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        py: 8,
        gap: 3
      }}
    >
      {/* Container do logo com animação */}
      <Box
        sx={{
          position: 'relative',
          width: 100,
          height: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {/* Círculo pulsante */}
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            backgroundColor: 'rgba(235, 93, 54, 0.1)',
            animation: 'pulseRing 2s ease-in-out infinite',
            '@keyframes pulseRing': {
              '0%': { transform: 'scale(0.95)', opacity: 1 },
              '50%': { transform: 'scale(1.15)', opacity: 0.6 },
              '100%': { transform: 'scale(0.95)', opacity: 1 }
            }
          }}
        />
        
        {/* Logo flutuante */}
        <Box
          component="img"
          src="/favicon.svg"
          alt="Advogada Parceira"
          sx={{
            width: 56,
            height: 56,
            zIndex: 1,
            animation: 'logoFloat 3s ease-in-out infinite',
            '@keyframes logoFloat': {
              '0%, 100%': { transform: 'translateY(0) scale(1)' },
              '50%': { transform: 'translateY(-8px) scale(1.05)' }
            }
          }}
        />
      </Box>
      
      {/* Texto e bolinhas */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
        <Typography 
          variant="h6" 
          sx={{ fontWeight: 600, color: '#EB5D36', textAlign: 'center' }}
        >
          Advogada Parceira
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 320 }}
        >
          Analisando seu documento...
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 320, mt: 0.5 }}
        >
          Nossa IA está identificando oportunidades de melhoria. Isso pode levar alguns segundos.
        </Typography>
        
        {/* Bolinhas animadas */}
        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mt: 2 }}>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #EB5D36 0%, #FF8F4E 100%)',
                animation: 'bounce 1.4s ease-in-out infinite',
                animationDelay: `${i * 0.2}s`,
                '@keyframes bounce': {
                  '0%, 80%, 100%': { transform: 'translateY(0)' },
                  '40%': { transform: 'translateY(-12px)' }
                }
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );

  // Render Suggestions Step
  const renderSuggestionsStep = () => {
    const groupedSuggestions = getSuggestionsByCategory();
    
    return (
      <Box>
        {/* Header com resumo */}
        <Paper sx={{ p: 3, mb: 3, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)` }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                  {analysisResult?.quality_score || 0}%
                </Typography>
                <Typography variant="body2" color="text.secondary">Score de Qualidade</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Typography variant="h6" gutterBottom>{analysisResult?.document_type}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {analysisResult?.document_summary}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip 
                  label={`${analysisResult?.statistics.total_words} palavras`} 
                  size="small" 
                  variant="outlined" 
                />
                <Chip 
                  label={`${analysisResult?.suggestions.length} sugestões`} 
                  size="small" 
                  color="primary" 
                />
                {getCriticalCount() > 0 && (
                  <Chip 
                    label={`${getCriticalCount()} críticas`} 
                    size="small" 
                    color="error" 
                  />
                )}
                {preserveStructure && structureInfo && (
                  <>
                    {structureInfo.tableCount > 0 && (
                      <Chip 
                        label={`${structureInfo.tableCount} tabela(s) preservada(s)`} 
                        size="small" 
                        color="success"
                        variant="outlined"
                      />
                    )}
                    {structureInfo.imageCount > 0 && (
                      <Chip 
                        label={`${structureInfo.imageCount} imagem(ns) preservada(s)`} 
                        size="small" 
                        color="success"
                        variant="outlined"
                      />
                    )}
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Lista de sugestões por categoria */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              Sugestões de Melhoria ({analysisResult?.suggestions.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                onClick={() => {
                  const allIds = new Set(analysisResult?.suggestions.map(s => s.id) || []);
                  setSelectedSuggestions(allIds);
                }}
              >
                Selecionar Todas
              </Button>
              <Button
                size="small"
                onClick={() => setSelectedSuggestions(new Set())}
              >
                Limpar
              </Button>
            </Box>
          </Box>

          {Object.entries(groupedSuggestions).map(([category, suggestions]) => {
            const config = categoryConfig[category as keyof typeof categoryConfig];
            return (
              <Accordion key={category} defaultExpanded={category === 'jurisprudencia' || category === 'legislacao'}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Box sx={{ color: config.color }}>{config.icon}</Box>
                    <Typography fontWeight="bold">{config.label}</Typography>
                    <Chip label={suggestions.length} size="small" sx={{ ml: 'auto', mr: 2 }} />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {suggestions.map((suggestion) => (
                      <ListItem
                        key={suggestion.id}
                        sx={{
                          border: '1px solid',
                          borderColor: selectedSuggestions.has(suggestion.id) 
                            ? theme.palette.primary.main 
                            : 'divider',
                          borderRadius: 2,
                          mb: 1,
                          backgroundColor: selectedSuggestions.has(suggestion.id)
                            ? alpha(theme.palette.primary.main, 0.05)
                            : 'transparent',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleToggleSuggestion(suggestion.id)}
                      >
                        <ListItemIcon>
                          <Checkbox
                            checked={selectedSuggestions.has(suggestion.id)}
                            color="primary"
                          />
                        </ListItemIcon>
                        <ListItemText
                          disableTypography
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" fontWeight="bold">
                                {suggestion.title}
                              </Typography>
                              <Chip
                                label={severityConfig[suggestion.severity].label}
                                color={severityConfig[suggestion.severity].color}
                                size="small"
                              />
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="caption" color="text.secondary" component="span" display="block">
                                {suggestion.description}
                              </Typography>
                              {suggestion.legal_basis && (
                                <Typography variant="caption" display="block" color="primary" component="span">
                                  Base legal: {suggestion.legal_basis}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedSuggestion(suggestion);
                              setPreviewOpen(true);
                            }}
                          >
                            <PreviewIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>

        {/* Botão de aplicar */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
          <Button
            variant="outlined"
            size="large"
            onClick={() => setActiveStep(0)}
          >
            Voltar
          </Button>
          <Button
            variant="contained"
            size="large"
            startIcon={isApplying ? <CircularProgress size={20} color="inherit" /> : <MagicIcon />}
            onClick={handleApplySuggestions}
            disabled={selectedSuggestions.size === 0 || isApplying}
          >
            Aplicar {getSelectedCount()} Melhorias
          </Button>
        </Box>
      </Box>
    );
  };

  // Render Final Step
  const renderFinalStep = () => (
    <Box>
      <Alert severity="success" sx={{ mb: 3 }}>
        <Typography variant="body1" fontWeight="bold">
          Documento melhorado com sucesso!
        </Typography>
        <Typography variant="body2">
          {getSelectedCount()} melhorias foram aplicadas ao seu documento.
        </Typography>
      </Alert>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Resumo das Melhorias Aplicadas
        </Typography>
        <Grid container spacing={2}>
          {Object.entries(getSuggestionsByCategory()).map(([category, suggestions]) => {
            const applied = suggestions.filter(s => selectedSuggestions.has(s.id)).length;
            if (applied === 0) return null;
            const config = categoryConfig[category as keyof typeof categoryConfig];
            return (
              <Grid item xs={6} sm={4} md={2} key={category}>
                <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: alpha(config.color, 0.1) }}>
                  <Box sx={{ color: config.color, mb: 1 }}>{config.icon}</Box>
                  <Typography variant="h5" fontWeight="bold" color={config.color}>{applied}</Typography>
                  <Typography variant="caption">{config.label}</Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* Informação sobre estrutura preservada */}
      {preserveStructure && structureInfo && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: alpha(theme.palette.success.main, 0.08), border: '1px solid', borderColor: alpha(theme.palette.success.main, 0.3) }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <CheckCircleIcon color="success" />
            <Typography variant="h6" fontWeight="bold" color="success.dark">
              Estrutura Original Preservada
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            O documento exportado manterá toda a formatação original, incluindo:
          </Typography>
          <Grid container spacing={2}>
            {structureInfo.tableCount > 0 && (
              <Grid item xs={6} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TableIcon color="success" />
                  <Typography variant="body2">
                    <strong>{structureInfo.tableCount}</strong> tabela(s)
                  </Typography>
                </Box>
              </Grid>
            )}
            {structureInfo.imageCount > 0 && (
              <Grid item xs={6} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ImageIcon color="success" />
                  <Typography variant="body2">
                    <strong>{structureInfo.imageCount}</strong> imagem(ns)
                  </Typography>
                </Box>
              </Grid>
            )}
            <Grid item xs={6} sm={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextIcon color="success" />
                <Typography variant="body2">
                  Formatação e estilos
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Aviso sobre preview não disponível para documentos com estrutura complexa */}
      {preserveStructure && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Nota:</strong> O preview de texto não está disponível para documentos com estrutura complexa (tabelas, imagens, listas).
            Baixe o documento DOCX para visualizar todas as melhorias com a formatação original preservada.
          </Typography>
        </Alert>
      )}

      {/* Preview removido - usuário baixa o documento diretamente para ver as melhorias */}

      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<EditIcon />}
          onClick={handleOpenSaveDialog}
          sx={{ minWidth: 200 }}
        >
          Abrir no Editor
        </Button>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => {
            setActiveStep(0);
            setUploadedFile(null);
            setExtractedText('');
            setAnalysisResult(null);
            setSelectedSuggestions(new Set());
            setImprovedContent('');
            setSessionId(null);
            setPreserveStructure(false);
            setStructureInfo(null);
          }}
        >
          Analisar Outro Documento
        </Button>
      </Box>
    </Box>
  );

  // Render Preview Dialog
  const renderPreviewDialog = () => (
    <Dialog
      open={previewOpen}
      onClose={() => setPreviewOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {selectedSuggestion && categoryConfig[selectedSuggestion.category]?.icon}
          <Typography variant="h6">{selectedSuggestion?.title}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {selectedSuggestion && (
          <Box>
            <Chip
              label={severityConfig[selectedSuggestion.severity].label}
              color={severityConfig[selectedSuggestion.severity].color}
              size="small"
              sx={{ mb: 2 }}
            />
            
            <Typography variant="body1" paragraph>
              {selectedSuggestion.description}
            </Typography>

            {selectedSuggestion.original_text && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="error" gutterBottom>
                  Texto Original:
                </Typography>
                <Paper sx={{ p: 2, bgcolor: alpha('#f44336', 0.05), border: '1px solid', borderColor: alpha('#f44336', 0.2) }}>
                  <Typography variant="body2" sx={{ textDecoration: 'line-through' }}>
                    {selectedSuggestion.original_text}
                  </Typography>
                </Paper>
              </Box>
            )}

            {selectedSuggestion.suggested_text && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="success.main" gutterBottom>
                  Texto Sugerido:
                </Typography>
                <Paper sx={{ p: 2, bgcolor: alpha('#4caf50', 0.05), border: '1px solid', borderColor: alpha('#4caf50', 0.2) }}>
                  <Typography variant="body2">
                    {selectedSuggestion.suggested_text}
                  </Typography>
                </Paper>
              </Box>
            )}

            {selectedSuggestion.legal_basis && (
              <Box>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Fundamento Legal:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedSuggestion.legal_basis}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPreviewOpen(false)}>Fechar</Button>
        <Button
          variant="contained"
          onClick={() => {
            if (selectedSuggestion) {
              handleToggleSuggestion(selectedSuggestion.id);
            }
            setPreviewOpen(false);
          }}
        >
          {selectedSuggestion && selectedSuggestions.has(selectedSuggestion.id) ? 'Desmarcar' : 'Selecionar'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        bgcolor: 'background.default',
        pb: 4
      }}
    >
      {/* Header Compacto */}
      <Box 
        sx={{ 
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'white',
          py: { xs: 2, md: 2.5 },
          px: { xs: 2, md: 4 },
          mb: 3,
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              startIcon={<BackIcon />}
              onClick={() => navigate('/documents')}
              size="small"
              sx={{ color: 'text.secondary' }}
            >
              Voltar
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 1 }}>
              <PsychologyIcon sx={{ fontSize: 28, color: 'primary.main' }} />
              <Box>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                    lineHeight: 1.2
                  }}
                >
                  Assistente de Melhorias
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary',
                    fontSize: { xs: '0.75rem', md: '0.875rem' }
                  }}
                >
                  Analise e aprimore seus documentos com IA
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4 } }}>
        {/* Stepper Responsivo */}
        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 2, md: 3 }, 
            mb: 4, 
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'white',
            overflowX: 'auto'
          }}
        >
          <Stepper 
            activeStep={activeStep}
            sx={{ 
              minWidth: { xs: 500, md: 'auto' },
              '& .MuiStepLabel-label': {
                fontSize: { xs: '0.75rem', md: '0.875rem' }
              },
              '& .MuiStepConnector-line': {
                borderTopWidth: 2
              },
              '& .MuiStepIcon-root': {
                fontSize: { xs: '1.5rem', md: '2rem' }
              },
              '& .MuiStepIcon-root.Mui-active': {
                color: 'primary.main'
              },
              '& .MuiStepIcon-root.Mui-completed': {
                color: 'success.main'
              }
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert 
            severity="error" 
            onClose={() => setError(null)} 
            sx={{ mb: 3, borderRadius: 2 }}
          >
            {error}
          </Alert>
        )}

        {/* Content */}
        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'white'
          }}
        >
          {isAnalyzing && renderAnalyzingStep()}
          {!isAnalyzing && activeStep === 0 && renderUploadStep()}
          {!isAnalyzing && activeStep === 2 && renderSuggestionsStep()}
          {!isAnalyzing && activeStep === 3 && renderFinalStep()}
        </Paper>
      </Container>

      {/* Preview Dialog */}
      {renderPreviewDialog()}

      {/* Diálogo de Salvar Documento */}
      <Dialog
        open={saveDialogOpen}
        onClose={() => !isSaving && setSaveDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SaveIcon color="primary" />
            <Typography variant="h6">Salvar Documento Melhorado</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            {/* Seleção de Estilo */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                Estilo de Formatação
              </Typography>
              <Grid container spacing={2}>
                {documentStyles.map((style) => (
                  <Grid item xs={12} sm={4} key={style.id}>
                    <Paper
                      onClick={() => setSelectedStyle(style.id as 'tradicional' | 'moderno' | 'minimalista')}
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        border: '2px solid',
                        borderColor: selectedStyle === style.id ? 'primary.main' : 'grey.200',
                        bgcolor: selectedStyle === style.id ? alpha(theme.palette.primary.main, 0.05) : 'background.paper',
                        borderRadius: 2,
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: selectedStyle === style.id ? 'primary.main' : 'grey.400',
                          boxShadow: 1
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="h6">{style.icon}</Typography>
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: 600,
                            color: selectedStyle === style.id ? 'primary.main' : 'text.primary'
                          }}
                        >
                          {style.name}
                        </Typography>
                        {selectedStyle === style.id && (
                          <CheckIcon sx={{ ml: 'auto', color: 'primary.main', fontSize: 20 }} />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                        {style.description}
                      </Typography>
                      <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary">
                          <strong>Fonte:</strong> {style.preview.font}<br />
                          <strong>Tamanho:</strong> {style.preview.size}<br />
                          <strong>Espaçamento:</strong> {style.preview.spacing}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider />

            {/* Nome e Pasta */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Nome do documento"
                  value={documentName}
                  onChange={(e) => setDocumentName(e.target.value)}
                  fullWidth
                  placeholder="Ex: Petição Inicial - Cliente X"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Pasta de destino</InputLabel>
                  <Select
                    value={selectedFolderId || ''}
                    onChange={(e) => setSelectedFolderId(e.target.value || null)}
                    label="Pasta de destino"
                    disabled={loadingFolders}
                  >
                    <MenuItem value="">
                      <em>📁 {DEFAULT_FOLDER_NAME} (padrão)</em>
                    </MenuItem>
                    <Divider />
                    {folders.map((folder) => (
                      <MenuItem key={folder.id} value={folder.id}>
                        <FolderIcon sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                        {folder.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <Alert severity="info" icon={<MagicIcon />}>
              <Typography variant="body2">
                O documento será formatado com o estilo <strong>{documentStyles.find(s => s.id === selectedStyle)?.name}</strong> e aberto no editor avançado.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setSaveDialogOpen(false)} 
            disabled={isSaving}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveToLibrary}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          >
            {isSaving ? 'Salvando...' : 'Salvar e Abrir'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Loading para Aplicar Melhorias */}
      <Dialog
        open={isApplying}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2,
            minWidth: 320,
            textAlign: 'center'
          }
        }}
      >
        <DialogContent>
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: 3,
              py: 2
            }}
          >
            {/* Container do logo com animação */}
            <Box
              sx={{
                position: 'relative',
                width: 100,
                height: 100,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {/* Círculo pulsante */}
              <Box
                sx={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(235, 93, 54, 0.1)',
                  animation: 'pulseRingImprove 2s ease-in-out infinite',
                  '@keyframes pulseRingImprove': {
                    '0%': { transform: 'scale(0.95)', opacity: 1 },
                    '50%': { transform: 'scale(1.15)', opacity: 0.6 },
                    '100%': { transform: 'scale(0.95)', opacity: 1 }
                  }
                }}
              />
              
              {/* Logo flutuante */}
              <Box
                component="img"
                src="/favicon.svg"
                alt="Advogada Parceira"
                sx={{
                  width: 56,
                  height: 56,
                  zIndex: 1,
                  animation: 'logoFloatImprove 3s ease-in-out infinite',
                  '@keyframes logoFloatImprove': {
                    '0%, 100%': { transform: 'translateY(0) scale(1)' },
                    '50%': { transform: 'translateY(-8px) scale(1.05)' }
                  }
                }}
              />
            </Box>
            
            {/* Texto e bolinhas */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
              <Typography 
                variant="h6" 
                sx={{ fontWeight: 600, color: '#EB5D36' }}
              >
                Advogada Parceira
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ color: 'text.secondary', textAlign: 'center' }}
              >
                Aplicando melhorias ao documento...
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 280, mt: 0.5 }}
              >
                Estamos processando {selectedSuggestions.size} {selectedSuggestions.size === 1 ? 'sugestão' : 'sugestões'} selecionadas.
              </Typography>
              
              {/* Bolinhas animadas */}
              <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mt: 2 }}>
                {[0, 1, 2].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #EB5D36 0%, #FF8F4E 100%)',
                      animation: 'bounceImprove 1.4s ease-in-out infinite',
                      animationDelay: `${i * 0.2}s`,
                      '@keyframes bounceImprove': {
                        '0%, 80%, 100%': { transform: 'translateY(0)' },
                        '40%': { transform: 'translateY(-12px)' }
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DocumentImprovementPage;

